import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Minus, Plus, ShoppingBag, Star, Leaf, Flame, Sprout } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { formatDualPrice } from '../utils/priceUtils';
import { formatMeasure } from '../utils/measureUtils';

const ProductModal = ({ item, isOpen, onClose }) => {
  const { getName, getDescription, t, language } = useLanguage();
  const { addItem } = useCart();
  const { isDark } = useTheme();
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);

  // Use image proxy for pizzadonvito.com images
  const getImageUrl = (url) => {
    if (url && url.includes('pizzadonvito.com')) {
      return `${process.env.REACT_APP_BACKEND_URL}/api/image-proxy?url=${encodeURIComponent(url)}`;
    }
    return url;
  };

  useEffect(() => {
    if (item && isOpen) {
      // Set default size
      const sizes = Object.keys(item.prices);
      if (sizes.includes('30cm')) {
        setSelectedSize('30cm');
      } else if (sizes.includes('default')) {
        setSelectedSize('default');
      } else if (sizes.length > 0) {
        setSelectedSize(sizes[0]);
      }
      setQuantity(1);
    }
  }, [item, isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!item) return null;

  // ⭐ ПЪЛЕН СПИСЪК С РАЗМЕРИ/ГРАМАЖИ ⭐
  const sizeLabels = {
    // Пици
    '24cm': { bg: 'Малка 24 см', en: 'Small 24cm' },
    '30cm': { bg: 'Голяма 30 см', en: 'Large 30cm' },
    '60cm': { bg: 'Семейна 60 см', en: 'Family 60cm' },
    
    // Салати и предястия (грамажи)
    '280г': { bg: '280 г', en: '280g' },
    '300г': { bg: '300 г', en: '300g' },
    '350г': { bg: '350 г', en: '350g' },
    '370г': { bg: '370 г', en: '370g' },
    '380г': { bg: '380 г', en: '380g' },
    '390г': { bg: '390 г', en: '390g' },
    '400г': { bg: '400 г', en: '400g' },
    '430г': { bg: '430 г', en: '430g' },
    '450г': { bg: '450 г', en: '450g' },
    '470г': { bg: '470 г', en: '470g' },
    '500г': { bg: '500 г', en: '500g' },
    '550г': { bg: '550 г', en: '550g' },
    '600г': { bg: '600 г', en: '600g' },
    '650г': { bg: '650 г', en: '650g' },
    '1кг': { bg: '1 кг', en: '1kg' },
    
    // Десерти
    '150г': { bg: '150 г', en: '150g' },
    '180г': { bg: '180 г', en: '180g' },
    '200г': { bg: '200 г', en: '200g' },
    '250г': { bg: '250 г', en: '250g' },
    '2бр': { bg: '2 бр.', en: '2 pcs.' },
    
    // Напитки
    '50мл': { bg: '50 мл', en: '50ml' },
    '150мл': { bg: '150 мл', en: '150ml' },
    '200мл': { bg: '200 мл', en: '200ml' },
    '250мл': { bg: '250 мл', en: '250ml' },
    '330мл': { bg: '330 мл', en: '330ml' },
    '500мл': { bg: '500 мл', en: '500ml' },
    '750мл': { bg: '750 мл', en: '750ml' },
    '1л': { bg: '1 л', en: '1l' },
    
    // Сосове
    '60г': { bg: '60 г', en: '60g' },
    '100г': { bg: '100 г', en: '100g' },
    
    // Добавки
    '1бр': { bg: '1 бр.', en: '1 pc.' },
    '3бр': { bg: '3 бр.', en: '3 pcs.' },
    '7.5мл': { bg: '7.5 мл', en: '7.5ml' },
    '15мл': { bg: '15 мл', en: '15ml' },
    
    // Специални
    'default': { bg: 'Стандартна', en: 'Standard' },
    'chocolate': { bg: 'С шоколад', en: 'With chocolate' },
    'jam': { bg: 'Със сладко', en: 'With jam' },
    'honey': { bg: 'С мед', en: 'With honey' },
    'cheese': { bg: 'Със сирене', en: 'With cheese' }
  };

  // Функция за показване на размера
  const getSizeDisplay = (sizeKey) => {
    const label = sizeLabels[sizeKey];
    if (label) {
      return language === 'bg' ? label.bg : label.en;
    }
    return formatMeasure(sizeKey, language);
  };

  const currentPrice = item.prices[selectedSize] || 0;
  const totalPrice = currentPrice * quantity;
  const hasSizes = Object.keys(item.prices).length > 1;

  const handleAddToCart = () => {
    addItem(item, selectedSize, quantity);
    onClose();
  };

  const tagIcons = {
    top: { icon: Star, label: 'Popular' },
    new: { icon: Flame, label: 'New' },
    vegan: { icon: Sprout, label: 'Vegan' },
    vegetarian: { icon: Leaf, label: 'Vegetarian' },
    spicy: { icon: Flame, label: 'Spicy' }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          data-testid="product-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className={`absolute inset-0 backdrop-blur-sm ${isDark ? 'bg-black/80' : 'bg-black/50'}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className={`relative w-full max-w-lg border sm:rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto scrollbar-hide ${
              isDark 
                ? 'bg-[#121212] border-white/10' 
                : 'bg-white border-gray-200'
            }`}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            initial={{ opacity: 0, y: 100, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Close Button */}
            <button
              data-testid="close-modal-btn"
              onClick={onClose}
              className={`absolute top-4 right-4 z-10 w-10 h-10 backdrop-blur-md rounded-full flex items-center justify-center transition-colors ${
                isDark 
                  ? 'bg-black/50 text-white/80 hover:text-white hover:bg-black/70' 
                  : 'bg-white/80 text-gray-600 hover:text-gray-900 hover:bg-white'
              }`}
            >
              <X size={20} />
            </button>

            {/* Image */}
            <div className="relative aspect-video">
              <img
                src={getImageUrl(item.image)}
                alt={getName(item)}
                className="w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              <div className={`absolute inset-0 bg-gradient-to-t via-transparent to-transparent ${
                isDark ? 'from-[#121212]' : 'from-white'
              }`} />
              
              {/* Tags on image */}
              {item.tags && item.tags.length > 0 && (
                <div className="absolute top-4 left-4 flex gap-2">
                  {item.tags.map(tag => {
                    const config = tagIcons[tag];
                    if (!config) return null;
                    const TagIcon = config.icon;
                    return (
                      <span 
                        key={tag}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-black/50 backdrop-blur-md rounded-full text-sm text-white"
                      >
                        <TagIcon size={14} className={
                          tag === 'top' ? 'text-yellow-400' : 
                          tag === 'vegan' ? 'text-emerald-400' :
                          tag === 'vegetarian' ? 'text-[#008000]' : 
                          tag === 'spicy' ? 'text-orange-400' : 'text-[#C8102E]'
                        } />
                        {config.label}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-6">
              <h2 className={`font-cormorant text-2xl sm:text-3xl font-bold mb-2 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                {getName(item)}
              </h2>
              
              <p className={`font-outfit mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {getDescription(item)}
              </p>

              {/* ⭐ РАЗМЕРИ/ГРАМАЖИ - ВИНАГИ СЕ ПОКАЗВАТ ⭐ */}
              <div className="mb-6">
                <h3 className={`font-outfit text-sm mb-3 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                  {t(hasSizes ? 'sizes' : 'size')}
                </h3>
                <div className="grid grid-cols-1 gap-2">
                  {Object.entries(item.prices).map(([sizeKey, price]) => {
                    const isSelected = selectedSize === sizeKey;
                    const sizeDisplay = getSizeDisplay(sizeKey);
                    
                    return (
                      <button
                        key={sizeKey}
                        data-testid={`size-${sizeKey}`}
                        onClick={() => setSelectedSize(sizeKey)}
                        className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all duration-300 ${
                          isSelected 
                            ? 'border-[#008000] bg-[#008000]/10' 
                            : isDark
                              ? 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white'
                              : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        <span className={`font-medium ${isSelected ? 'text-[#008000]' : ''}`}>
                          {sizeDisplay}
                        </span>
                        <span className={`font-bold ${isSelected ? 'text-[#C8102E]' : ''}`}>
                          {formatDualPrice(price).eur} € / {formatDualPrice(price).bgn} {t('currency')}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <h3 className={`font-outfit text-sm mb-3 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                  {t('quantity')}
                </h3>
                <div className="flex items-center gap-4">
                  <button
                    data-testid="decrease-qty"
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      isDark 
                        ? 'bg-white/10 text-white hover:bg-white/20' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Minus size={18} />
                  </button>
                  <span data-testid="quantity-value" className={`font-outfit text-xl font-bold w-8 text-center ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {quantity}
                  </span>
                  <button
                    data-testid="increase-qty"
                    onClick={() => setQuantity(q => q + 1)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      isDark 
                        ? 'bg-white/10 text-white hover:bg-white/20' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              {/* Add to Cart Button */}
              <motion.button
                data-testid="add-to-cart-modal"
                onClick={handleAddToCart}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#008000] text-white font-semibold rounded-full hover:bg-[#009900] transition-colors shadow-[0_0_20px_rgba(0,128,0,0.3)]"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <ShoppingBag size={20} />
                <span>{t('add_to_cart')}</span>
                <span className="ml-auto font-bold">
                  {formatDualPrice(totalPrice).eur} € / {formatDualPrice(totalPrice).bgn} {t('currency')}
                </span>
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductModal;

